import { StyleSheetManager } from 'styled-components';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import isPropValid from '@emotion/is-prop-valid';
import * as Sentry from '@sentry/react';
import { registerSW } from 'virtual:pwa-register';

import Root from './components/root';
import I18nProvider from './i18n/i18n_provider';
import JotaiProvider from './jotai_provider';

import './polyfill';

import { MODE, PROD } from './env';

import type { ShouldForwardProp } from 'styled-components';

const isProduction = PROD;

registerSW({
  immediate: true,
  onRegistered(registration) {
    if (registration) {
      setInterval(
        () => {
          registration.update();
        },
        1000 * 60, // 1 minute
      );
    }
  },
});

if (isProduction) {
  Sentry.init({
    dsn: 'https://38deec1516cd485fb1607b115bfd95b7@o460867.ingest.sentry.io/4505125173460992',
    environment: MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
  });
}

const root = document.getElementById('root');

if (!root) {
  throw new Error('Could not find root element');
}

const shouldForwardProp: ShouldForwardProp<'web'> = (propName, target) => {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }

  // For other elements, forward all props
  return true;
};

createRoot(root).render(
  <StrictMode>
    <JotaiProvider>
      <Router future={{ v7_relativeSplatPath: false, v7_startTransition: true }}>
        <I18nProvider>
          <StyleSheetManager shouldForwardProp={shouldForwardProp}>
            <Root />
          </StyleSheetManager>
        </I18nProvider>
      </Router>
    </JotaiProvider>
  </StrictMode>,
);
